import React, { useState, useEffect } from 'react';
import { RegulatoryChange, RegulatoryImpactAssessment } from '../types';
import { regulatoryService } from '../regulatoryService';

interface RegulatoryChangesTabProps {
  onRefresh: () => void;
}

export const RegulatoryChangesTab: React.FC<RegulatoryChangesTabProps> = ({ onRefresh }) => {
  const [changes, setChanges] = useState<RegulatoryChange[]>([]);
  const [selectedChange, setSelectedChange] = useState<RegulatoryChange | null>(null);
  const [loading, setLoading] = useState(false);

  // Impact Assessment Form
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessorName, setAssessorName] = useState('Safety & Compliance Director');
  const [affectedDepts, setAffectedDepts] = useState('Flight Operations, Dispatch, Training');
  const [affectedManuals, setAffectedManuals] = useState('OMM Part A, Dispatch Manual');
  const [reqMoc, setReqMoc] = useState(true);
  const [reqTraining, setReqTraining] = useState(true);
  const [reqAudit, setReqAudit] = useState(false);
  const [reqRisk, setReqRisk] = useState(true);
  const [summary, setSummary] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadChanges();
  }, []);

  const loadChanges = async () => {
    try {
      setLoading(true);
      const data = await regulatoryService.getChanges();
      setChanges(data);
      if (data.length > 0) {
        loadChangeDetail(data[0].id);
      }
    } catch (err) {
      console.error('Error loading regulatory changes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChangeDetail = async (id: string) => {
    try {
      const detail = await regulatoryService.getChangeById(id);
      setSelectedChange(detail);
    } catch (err) {
      console.error('Error loading change detail:', err);
    }
  };

  const handleCreateAssessment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChange) return;
    try {
      setSaving(true);
      await regulatoryService.createImpactAssessment({
        changeId: selectedChange.id,
        organizationId: 'ORG-XYZ',
        assessorId: 'COMPLIANCE_DIR_01',
        assessorName,
        affectedDepartments: affectedDepts,
        affectedManuals,
        requiresMoc: reqMoc,
        requiresTraining: reqTraining,
        requiresAudit: reqAudit,
        requiresRiskAssessment: reqRisk,
        summary,
      });
      setShowAssessmentModal(false);
      setSummary('');
      await loadChangeDetail(selectedChange.id);
      onRefresh();
    } catch (err: any) {
      alert(err.message || 'Error creating impact assessment.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveAssessment = async (assessmentId: string) => {
    if (!selectedChange) return;
    try {
      await regulatoryService.approveImpactAssessment(assessmentId, 'ACCOUNTABLE_EXECUTIVE');
      await loadChangeDetail(selectedChange.id);
      onRefresh();
    } catch (err: any) {
      alert(err.message || 'Error approving assessment.');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column: Changes Feed */}
      <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <h3 className="font-bold text-gray-900 text-sm flex items-center justify-between">
          <span>Published Amendments & NPA Feed</span>
          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-mono">
            {changes.length} Records
          </span>
        </h3>
        <p className="text-xs text-gray-500">
          Effective implementation timelines published by EASA, ICAO, and National Aviation Authorities.
        </p>

        <div className="space-y-2 mt-3">
          {changes.map((c) => (
            <div
              key={c.id}
              onClick={() => loadChangeDetail(c.id)}
              className={`p-3 rounded-lg border transition cursor-pointer text-xs ${
                selectedChange?.id === c.id
                  ? 'border-blue-500 bg-blue-50/40 shadow-xs'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="font-bold text-gray-700">{c.documentCode}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                  c.status === 'ASSESSED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                }`}>
                  {c.status}
                </span>
              </div>
              <h4 className="font-semibold text-gray-900 line-clamp-1">{c.title}</h4>
              <div className="flex justify-between items-center text-[11px] text-gray-400 mt-2 font-mono">
                <span>Effective: {c.effectiveDate.substring(0, 10)}</span>
                <span className="text-blue-600 font-sans font-medium">{c.changeType}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Column: Change Details, Delta Diff, & Impact Assessment */}
      <div className="lg:col-span-2 space-y-6">
        {selectedChange ? (
          <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-5">
            {/* Header */}
            <div className="flex justify-between items-start border-b border-gray-100 pb-4">
              <div>
                <span className="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                  {selectedChange.changeType}
                </span>
                <h2 className="text-base font-bold text-gray-900 mt-1">{selectedChange.title}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{selectedChange.summary}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAssessmentModal(true)}
                className="btn-primary"
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                + Start Impact Assessment
              </button>
            </div>

            {/* Delta Diffs */}
            <div>
              <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">
                Requirement Deltas & Modified Articles
              </h4>
              <div className="space-y-3">
                {selectedChange.requirementChanges && selectedChange.requirementChanges.length > 0 ? (
                  selectedChange.requirementChanges.map((delta) => (
                    <div key={delta.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-mono font-bold text-gray-800">
                          {delta.requirementCode} — {delta.requirementTitle}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          delta.impactLevel === 'CRITICAL' ? 'bg-red-600 text-white' : delta.impactLevel === 'MAJOR' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {delta.impactLevel} IMPACT
                        </span>
                      </div>
                      <p className="text-gray-700 font-medium">{delta.diffSummary}</p>
                      {delta.oldText && (
                        <div className="p-2 bg-red-50 border border-red-200 text-red-900 rounded font-serif text-[11px] line-through">
                          {delta.oldText}
                        </div>
                      )}
                      {delta.newText && (
                        <div className="p-2 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded font-serif text-[11px]">
                          {delta.newText}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-400 italic">No requirement-level diffs recorded.</div>
                )}
              </div>
            </div>

            {/* Existing Impact Assessments */}
            <div>
              <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">
                Evaluated Impact Assessments
              </h4>
              <div className="space-y-3">
                {selectedChange.impactAssessments && selectedChange.impactAssessments.length > 0 ? (
                  selectedChange.impactAssessments.map((asmt) => (
                    <div key={asmt.id} className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-900">
                          Assessor: {asmt.assessorName}
                        </span>
                        <span className={`px-2 py-0.5 rounded font-bold text-[10px] ${
                          asmt.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {asmt.status}
                        </span>
                      </div>
                      <p className="text-gray-700">{asmt.summary}</p>
                      <div className="grid grid-cols-2 gap-2 text-gray-500 text-[11px] pt-1">
                        <div><strong>Departments:</strong> {asmt.affectedDepartments || 'None'}</div>
                        <div><strong>Manuals:</strong> {asmt.affectedManuals || 'None'}</div>
                      </div>
                      <div className="flex gap-2 pt-2 text-[10px] font-semibold">
                        {asmt.requiresMoc && <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded">MoC Mandatory</span>}
                        {asmt.requiresTraining && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">Training Required</span>}
                        {asmt.requiresRiskAssessment && <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded">Risk Assessment</span>}
                        {asmt.requiresAudit && <span className="px-2 py-0.5 bg-rose-100 text-rose-800 rounded">Audit Verification</span>}
                      </div>
                      {asmt.status === 'PENDING_APPROVAL' && (
                        <div className="pt-2 flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleApproveAssessment(asmt.id)}
                            className="btn-primary"
                            style={{ fontSize: '11px', padding: '4px 10px' }}
                          >
                            Approve Impact Assessment
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-400 italic">No approved impact assessment for this change yet.</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400 text-xs">
            Select a regulatory amendment from the left to view deltas and impact assessments.
          </div>
        )}
      </div>

      {/* Impact Assessment Modal */}
      {showAssessmentModal && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3>Initiate Regulatory Impact Assessment</h3>
                <p>Evaluate organizational exposure, operational manuals, and governance workflows</p>
              </div>
              <button type="button" onClick={() => setShowAssessmentModal(false)} className="close-btn" aria-label="Close modal">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAssessment}>
              <div className="modal-body">
                <div className="form-field">
                  <label>Assessor Name & Role *</label>
                  <input
                    type="text"
                    required
                    value={assessorName}
                    onChange={(e) => setAssessorName(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Affected Operational Departments</label>
                  <input
                    type="text"
                    value={affectedDepts}
                    onChange={(e) => setAffectedDepts(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Affected Manuals & Procedures</label>
                  <input
                    type="text"
                    value={affectedManuals}
                    onChange={(e) => setAffectedManuals(e.target.value)}
                  />
                </div>

                <div className="form-grid-2">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' }}>
                    <input type="checkbox" checked={reqMoc} onChange={(e) => setReqMoc(e.target.checked)} />
                    <span>Management of Change (MoC)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' }}>
                    <input type="checkbox" checked={reqTraining} onChange={(e) => setReqTraining(e.target.checked)} />
                    <span>Personnel Training Required</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' }}>
                    <input type="checkbox" checked={reqRisk} onChange={(e) => setReqRisk(e.target.checked)} />
                    <span>Safety Risk Assessment</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' }}>
                    <input type="checkbox" checked={reqAudit} onChange={(e) => setReqAudit(e.target.checked)} />
                    <span>Special Compliance Audit</span>
                  </label>
                </div>

                <div className="form-field">
                  <label>Executive Impact Summary *</label>
                  <textarea
                    required
                    rows={3}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="Describe operational, airworthiness, training, and commercial impacts..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowAssessmentModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? 'Saving...' : 'Submit Impact Assessment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
